import { useState } from "react";
import { DropZone } from "./SectionWidgets";
import { fmtDate, CORRESPONDENCE_PROMPT_CATEGORY } from "./shared";
import { extractQuoteFromPdf, checkQuoteDuplicate, writeQuoteToSQL } from "./quoteScrub";
import { parseMoney, sameAmount, fmtMoney } from "./releaseCheck";

export default function MultiDocPanel(props) {
  const {
  // (section / addDocForm / docUploading are accepted for callers but unused here)
  documents, docTypes, canUpload, uploadLockMessage,
  showAddDoc, setShowAddDoc,
  onUpload, onDelete,
  // Optional: change an uploaded document's Type (onChangeType(doc, type));
  // lockedDocIds are documents something else already points at.
  onChangeType = null, lockedDocIds = [],
  notesText, setNotesText, notesDirty, setNotesDirty, saveNotes, savingNotes,
  onAnalyzeDoc, analyzingDoc, contractJobStatus, docAiSummaries,
  onShowDocSummary, onShowCombinedSummary, hasCombinedSummary, onRunCombined,
  enableQuoteScrub, turnover, currentUser, aiPrefixBase = "contract_review",
  requireValueForDocType = null,
  // Optional: split the log into areas (§7 — one per HubSpot release + General).
  // Each group: { key, title, subtitle, documents, uploadMeta, expectedValue, isGeneral }.
  groups = null,
  // Set on a group's own panel:
  title = "Documents", subtitle = "", uploadMeta = null, expectedValue = null, hideNotes = false,
  } = props;
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  // A grouped area owns its "add documents" state; the single-log panel keeps
  // using the parent's (showAddDoc / setShowAddDoc props).
  const [localShowAdd, setLocalShowAdd] = useState(false);
  const ownAddState = !!groups === false && uploadMeta !== null;
  const showAdd = ownAddState ? localShowAdd : showAddDoc;
  const setShowAdd = ownAddState ? setLocalShowAdd : setShowAddDoc;
  const inputStyle = {
    padding: "6px 10px", fontSize: 12, border: "1px solid var(--cs-border)",
    borderRadius: 6, background: "var(--surface)", color: "var(--text)",
    boxSizing: "border-box", fontFamily: "inherit",
  };

  const totalValue = documents.reduce((sum, d) => {
    const v = parseFloat(d.DocValue);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  function handleCancel() {
    setStagedFiles([]);
    setShowAdd(false);
  }

  async function runQuoteScrub(id, file) {
    setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, quoteStatus: "checking" } : f));
    try {
      const quoteData = await extractQuoteFromPdf(file);
      let quoteDupId = null;
      if (quoteData.vendor_name && quoteData.quote_date) {
        quoteDupId = await checkQuoteDuplicate(quoteData.vendor_name, quoteData.quote_date);
      }
      setStagedFiles(prev => prev.map(f => {
        if (f.id !== id) return f;
        return {
          ...f,
          quoteStatus: "ready",
          quoteData,
          quoteDupId,
          saveToQuotesDb: !quoteDupId,
          doc_type:    f.doc_type    || "Vendor Quote",
          description: f.description || quoteData.vendor_name || "",
          doc_date:    f.doc_date    || quoteData.quote_date  || "",
          doc_value:   f.doc_value   || (quoteData.total_price != null ? String(quoteData.total_price) : ""),
        };
      }));
    } catch (e) {
      console.warn("[Quote Scrub] Extraction failed:", e.message);
      setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, quoteStatus: "error", quoteError: e.message } : f));
    }
  }

  function addStagedFile(file) {
    const id = `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`;
    setStagedFiles(prev => [...prev, {
      id, file, doc_type: "", description: "", doc_date: "", doc_value: "",
      status: "pending", error: null,
      quoteStatus: "none", quoteData: null, quoteDupId: null, saveToQuotesDb: false,
    }]);
    if (enableQuoteScrub && file.type === "application/pdf") {
      runQuoteScrub(id, file);
    }
  }

  function removeStaged(id) { setStagedFiles(prev => prev.filter(f => f.id !== id)); }
  function updateStagedField(id, field, value) { setStagedFiles(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f)); }

  async function handleUploadAll() {
    const pending = stagedFiles.filter(f => f.status !== "done");
    if (pending.length === 0) return;
    // An Estimate's value is what §7 reconciles against the HubSpot releases,
    // and there's no edit-after-upload for it — require it up front.
    if (requireValueForDocType) {
      const noValue = pending.filter(f => f.doc_type === requireValueForDocType && parseMoney(f.doc_value) === null);
      if (noValue.length > 0) {
        setStagedFiles(prev => prev.map(f => noValue.some(n => n.id === f.id)
          ? { ...f, status: "error", error: `enter the ${requireValueForDocType.toLowerCase()} total in Value ($)` } : f));
        return;
      }
      // In a release's area the Estimate must equal that release exactly.
      if (expectedValue !== null && expectedValue !== undefined) {
        const off = pending.filter(f => f.doc_type === requireValueForDocType && !sameAmount(parseMoney(f.doc_value), expectedValue));
        if (off.length > 0) {
          setStagedFiles(prev => prev.map(f => off.some(n => n.id === f.id)
            ? { ...f, status: "error", error: `the estimate total must equal this release exactly (${fmtMoney(expectedValue)}) — check the estimate sheet or the HubSpot release amount` } : f));
          return;
        }
      }
    }
    setUploadingAll(true);
    let anyFailed = false;
    for (const sf of pending) {
      setStagedFiles(prev => prev.map(f => f.id === sf.id ? { ...f, status: "uploading", error: null } : f));
      try {
        const result = await onUpload(sf.file, {
          doc_type: sf.doc_type, description: sf.description,
          doc_date: sf.doc_date, doc_value: sf.doc_value,
          ...(uploadMeta || {}),
        });
        if (enableQuoteScrub && sf.quoteData && sf.saveToQuotesDb) {
          try {
            await writeQuoteToSQL({
              ...sf.quoteData,
              source_file:     sf.file.name,
              source_file_url: result?.webUrl || null,
              hubspot_deal_id: turnover?.HubSpotDealID || null,
              loaded_by_name:  currentUser?.name || null,
              loaded_by_email: currentUser?.email || null,
            });
          } catch (qe) {
            console.warn("[Quote Scrub] sp_WriteQuote failed:", qe.message);
            setStagedFiles(prev => prev.map(f => f.id === sf.id ? { ...f, quoteSaveError: qe.message } : f));
          }
        }
        setStagedFiles(prev => prev.map(f => f.id === sf.id ? { ...f, status: "done" } : f));
      } catch (e) {
        anyFailed = true;
        setStagedFiles(prev => prev.map(f => f.id === sf.id ? { ...f, status: "error", error: e.message } : f));
      }
    }
    setUploadingAll(false);
    if (!anyFailed) {
      setStagedFiles([]);
      setShowAdd(false);
    }
  }

  const doneCount = stagedFiles.filter(f => f.status === "done").length;

  if (groups && groups.length) {
    const last = groups.length - 1;
    return (
      <div>
        {groups.map((g, i) => (
          <div key={g.key} style={{ border: "1px solid var(--cs-border)", borderRadius: 8, padding: "12px 14px 2px", marginBottom: 14, background: g.isGeneral ? "var(--surface)" : "#f8fafc" }}>
            <MultiDocPanel
              {...props}
              groups={null}
              documents={g.documents}
              title={g.title}
              subtitle={g.subtitle}
              uploadMeta={g.uploadMeta || {}}
              expectedValue={g.expectedValue ?? null}
              hideNotes={i !== last}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Document log */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>
            {title}
            {subtitle && <div style={{ fontSize: 11, fontWeight: 400, color: "var(--text3)", marginTop: 2 }}>{subtitle}</div>}
            {documents.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: "var(--text3)" }}>
                {documents.length} file{documents.length !== 1 ? "s" : ""}
                {totalValue > 0 && ` · $${totalValue.toLocaleString("en-US", { maximumFractionDigits: 0 })} total`}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {aiPrefixBase === CORRESPONDENCE_PROMPT_CATEGORY ? (
              docAiSummaries?.some(s => s.SummaryType?.includes(`${CORRESPONDENCE_PROMPT_CATEGORY}_doc_`)) && (
                <button onClick={onShowCombinedSummary}
                  style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                  🕐 View Timeline ↗
                </button>
              )
            ) : hasCombinedSummary ? (
              <button onClick={onShowCombinedSummary}
                style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                Combined Summary ↗
              </button>
            ) : onRunCombined && docAiSummaries?.some(s => s.SummaryType?.includes("contract_review_doc_")) ? (
              <button onClick={onRunCombined} disabled={!!analyzingDoc}
                style={{ fontSize: 12, color: analyzingDoc === "combined" ? "#9ca3af" : "#7c3aed", fontWeight: 600, background: "none", border: "none", cursor: analyzingDoc ? "not-allowed" : "pointer", padding: 0, whiteSpace: "nowrap" }}>
                {analyzingDoc === "combined" ? "Generating…" : "Generate Combined ↗"}
              </button>
            ) : null}
            {canUpload && !showAdd && (
              <button
                onClick={() => setShowAdd(true)}
                style={{ padding: "5px 14px", fontSize: 12, fontWeight: 700, background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
              >
                + Add Document(s)
              </button>
            )}
          </div>
        </div>

        {showAdd && canUpload && (
          <div style={{ padding: "16px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, marginBottom: 14 }}>
            <div style={{ marginBottom: stagedFiles.length > 0 ? 14 : 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#0369a1", marginBottom: 8 }}>
                {stagedFiles.length === 0 ? "Step 1 — Select or drop your files" : "Add more files to this batch"}
              </div>
              <DropZone onFile={addStagedFile} uploading={false} multiple />
            </div>

            {stagedFiles.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#0369a1", marginBottom: 8 }}>
                  Step 2 — Fill in details (optional) for each file, then upload them all at once
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                  {stagedFiles.map(sf => (
                    <div key={sf.id} style={{
                      padding: "10px 14px", background: "#fff",
                      border: `1px solid ${sf.status === "error" ? "#fca5a5" : sf.status === "done" ? "#86efac" : "#bae6fd"}`,
                      borderRadius: 8,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 18 }}>
                          {sf.status === "done" ? "✅" : sf.status === "uploading" ? "⏳" : sf.status === "error" ? "⚠️" : "📎"}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{sf.file.name}</div>
                          <div style={{ fontSize: 11, color: sf.status === "error" ? "#dc2626" : "var(--text3)" }}>
                            {sf.status === "uploading" ? "Uploading to SharePoint…"
                              : sf.status === "done" ? "Uploaded"
                              : sf.status === "error" ? `Failed — ${sf.error}`
                              : `${(sf.file.size / 1024).toFixed(0)} KB — ready to upload`}
                          </div>
                        </div>
                        {sf.status !== "uploading" && sf.status !== "done" && (
                          <button onClick={() => removeStaged(sf.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text3)" }}>
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      {sf.status !== "done" && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 3 }}>Type</label>
                            <select value={sf.doc_type} disabled={sf.status === "uploading"}
                              onChange={e => updateStagedField(sf.id, "doc_type", e.target.value)}
                              style={{ ...inputStyle, width: "100%" }}>
                              <option value="">Select type…</option>
                              {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 3 }}>Reference / Description</label>
                            <input value={sf.description} disabled={sf.status === "uploading"}
                              onChange={e => updateStagedField(sf.id, "description", e.target.value)}
                              placeholder="e.g. PO-2026-0441" style={{ ...inputStyle, width: "100%" }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 3 }}>Date</label>
                            <input type="date" value={sf.doc_date} disabled={sf.status === "uploading"}
                              onChange={e => updateStagedField(sf.id, "doc_date", e.target.value)}
                              style={{ ...inputStyle, width: "100%" }} />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", display: "block", marginBottom: 3 }}>Value ($)</label>
                            <input value={sf.doc_value} disabled={sf.status === "uploading"}
                              onChange={e => updateStagedField(sf.id, "doc_value", e.target.value)}
                              placeholder="e.g. 500000" style={{ ...inputStyle, width: "100%" }} />
                          </div>
                        </div>
                      )}

                      {enableQuoteScrub && sf.file.type === "application/pdf" && sf.status !== "done" && (
                        <div style={{ marginTop: 10 }}>
                          {sf.quoteStatus === "checking" && (
                            <div style={{ fontSize: 11, color: "#0369a1", display: "flex", alignItems: "center", gap: 6 }}>
                              <div className="spinner" style={{ width: 11, height: 11 }} />
                              Checking for duplicates &amp; extracting quote data…
                            </div>
                          )}
                          {sf.quoteStatus === "error" && (
                            <div style={{ fontSize: 11, color: "#dc2626" }}>
                              ⚠️ Quote extraction failed ({sf.quoteError}) — document will still upload normally.
                            </div>
                          )}
                          {sf.quoteStatus === "ready" && sf.quoteDupId && (
                            <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "8px 12px", fontSize: 11, color: "#92400e", marginBottom: 8 }}>
                              ⚠️ Possible duplicate — a quote from <strong>{sf.quoteData.vendor_name}</strong> dated {sf.quoteData.quote_date} already exists (Quote #{sf.quoteDupId}).
                            </div>
                          )}
                          {sf.quoteStatus === "ready" && (
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text2)", cursor: "pointer" }}>
                              <input type="checkbox" checked={!!sf.saveToQuotesDb}
                                onChange={e => updateStagedField(sf.id, "saveToQuotesDb", e.target.checked)} />
                              Also save to Quotes database
                              {sf.quoteData.vendor_name && (
                                <span style={{ color: "var(--text3)" }}>
                                  — {sf.quoteData.vendor_name}{sf.quoteData.category ? ` · ${sf.quoteData.category}` : ""}{sf.quoteData.total_price != null ? ` · $${Number(sf.quoteData.total_price).toLocaleString()}` : ""}
                                </span>
                              )}
                            </label>
                          )}
                          {sf.quoteSaveError && (
                            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4 }}>
                              ⚠️ Document uploaded, but saving to the Quotes database failed: {sf.quoteSaveError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {stagedFiles.length > 0 && (
                <button
                  onClick={handleUploadAll}
                  disabled={uploadingAll || stagedFiles.every(f => f.status === "done")}
                  style={{
                    padding: "8px 20px", fontSize: 13, fontWeight: 700,
                    background: "var(--cs-blue)", color: "#fff",
                    border: "none", borderRadius: 6, cursor: "pointer",
                    opacity: (uploadingAll || stagedFiles.every(f => f.status === "done")) ? 0.5 : 1,
                  }}
                >
                  {uploadingAll ? `Uploading ${doneCount + 1} of ${stagedFiles.length}…` : `⬆ Upload All (${stagedFiles.length})`}
                </button>
              )}
              <button onClick={handleCancel} disabled={uploadingAll} style={{
                padding: "8px 16px", fontSize: 13, fontWeight: 600,
                background: "none", border: "1px solid var(--cs-border)",
                borderRadius: 6, cursor: uploadingAll ? "not-allowed" : "pointer", color: "var(--text2)",
                opacity: uploadingAll ? 0.5 : 1,
              }}>
                {stagedFiles.some(f => f.status === "error") ? "Cancel remaining" : "Cancel"}
              </button>
            </div>
          </div>
        )}

        {!canUpload && (
          <div style={{ padding: 12, background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8, fontSize: 12, color: "#92400e", marginBottom: 12 }}>
            {uploadLockMessage || "🔒 Uploads unlock once Accounting assigns a job number."}
          </div>
        )}

        {documents.length === 0 && (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
            No documents yet — click "+ Add Document(s)" to upload the first one.
          </div>
        )}
        {documents.map((doc, i) => (
          <div key={doc.TurnoverDocumentID} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 14px", marginBottom: 6,
            background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 8,
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>📄</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                {/* Not on §7: an Estimate there must carry a value that reconciles to its release, and a retype would skip that check. */}
                {onChangeType && canUpload && !requireValueForDocType && !lockedDocIds.includes(doc.TurnoverDocumentID) ? (
                  <select
                    value={doc.DocType || ""}
                    onChange={e => e.target.value && e.target.value !== doc.DocType && onChangeType(doc, e.target.value)}
                    title="Change this document's type"
                    style={{
                      fontSize: 11, fontWeight: 700, padding: "1px 4px", borderRadius: 100, cursor: "pointer", maxWidth: 170,
                      border: doc.DocType ? "1px solid transparent" : "1px solid #f59e0b",
                      background: doc.DocType ? "#dbeafe" : "#fffbeb",
                      color: doc.DocType ? "#1e40af" : "#92400e",
                    }}
                  >
                    {!doc.DocType && <option value="">No type — set type…</option>}
                    {doc.DocType && !docTypes.includes(doc.DocType) && <option value={doc.DocType}>{doc.DocType}</option>}
                    {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                ) : doc.DocType && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: "#dbeafe", color: "#1e40af", padding: "1px 7px", borderRadius: 100 }}>
                    {doc.DocType}
                  </span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{doc.FileName}</span>
                {doc.DocValue && (
                  <span style={{ fontSize: 12, color: "#059669", fontWeight: 600 }}>
                    ${parseFloat(doc.DocValue).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 3 }}>
                {doc.Description && <span style={{ fontSize: 11, color: "var(--text2)" }}>{doc.Description}</span>}
                {doc.DocDate && <span style={{ fontSize: 11, color: "var(--text3)" }}>{fmtDate(doc.DocDate)}</span>}
                <span style={{ fontSize: 11, color: "var(--text3)" }}>by {doc.UploadedBy}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
              {(() => {
                const typePrefix = `${aiPrefixBase}_doc_${doc.TurnoverDocumentID}`;
                const hasThisDocSummary = docAiSummaries?.some(s =>
                  s.SummaryType?.startsWith(typePrefix + "_s")
                );
                const isAnalyzing = analyzingDoc === doc.TurnoverDocumentID;
                const isPolling = contractJobStatus === "Processing" && !isAnalyzing;
                // For contract_review: analysis is saved combined across the whole section
                // (contract_review_combined_s*), never per-document — so gate on the combined
                // rows alone. Requiring a per-doc row here made "View Summary" revert to
                // "Analyze" on reload even though the analysis was saved.
                const hasCombined = aiPrefixBase === "contract_review"
                  ? docAiSummaries?.some(s => s.SummaryType?.startsWith("contract_review_combined_s"))
                  : hasThisDocSummary;

                if (onAnalyzeDoc) {
                  // View Summary — analysis complete
                  if (hasCombined || contractJobStatus === "Complete") return (
                    <button onClick={() => onShowDocSummary(
                      aiPrefixBase === "contract_review" ? "combined_contract_review" : doc.TurnoverDocumentID
                    )}
                      style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                      View Summary ↗
                    </button>
                  );
                  // Processing — polling in background
                  if (isAnalyzing || isPolling) return (
                    <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {isAnalyzing ? "Submitting…" : "Processing…"}
                    </span>
                  );
                  // Default — Analyze button
                  return (
                    <button onClick={() => onAnalyzeDoc(doc)} disabled={!!analyzingDoc || isPolling}
                      style={{ fontSize: 12, color: "#0369a1", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, whiteSpace: "nowrap" }}>
                      Analyze ↗
                    </button>
                  );
                }
              })()}
              {doc.SharePointUrl && (
                <a href={doc.SharePointUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: "var(--cs-blue)", fontWeight: 600, textDecoration: "none" }}>
                  View ↗
                </a>
              )}
              <button onClick={() => onDelete(doc.TurnoverDocumentID)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#d1d5db", padding: "0 4px" }}
                onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}>
                🗑
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Section Notes */}
      {!hideNotes && <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>Section Notes</div>
          {notesDirty && (
            <button onClick={saveNotes} disabled={savingNotes}
              style={{ padding: "4px 12px", fontSize: 12, fontWeight: 600, background: "var(--cs-blue)", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", opacity: savingNotes ? 0.5 : 1 }}>
              {savingNotes ? "Saving…" : "Save Notes"}
            </button>
          )}
        </div>
        <textarea value={notesText} onChange={e => { setNotesText(e.target.value); setNotesDirty(true); }} onBlur={saveNotes}
          placeholder="Add section notes…" rows={3}
          style={{ width: "100%", padding: "10px 12px", fontSize: 13, border: "1px solid " + (notesDirty ? "var(--cs-blue)" : "var(--cs-border)"), borderRadius: 8, resize: "vertical", boxSizing: "border-box", background: "var(--surface)", color: "var(--text)", fontFamily: "inherit", lineHeight: 1.5, outline: "none" }} />
      </div>}
    </div>
  );
}
